import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../../utils/texts';
import './VotingDataCardsFilter.css';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DropdownOptionType } from '../../../types';
import { URLKeys } from '../../../Constants';

type VotingFormDataType = {
    [URLKeys.voteDescription]: string;
    [URLKeys.meetingNumber]: number | null; // should be number, will be unified in the future
    [URLKeys.memberNumber]: number | null;
    [URLKeys.memberVote]: 'all' | '1' | '2' | number;
    [URLKeys.voteResult]: 'all' | '1' | '2';
    [URLKeys.sortBy]: 'nejnovejsi' | 'nejstarsi';
    [URLKeys.show]: string[]; // replacement for checkboxes (validVotes, nonProceduralVotes, publicVotes)
};

type DropdownOptionsType = {
    meeting: { label: string; value: number }[];
    member: { label: string; value: number }[];
    otherVoteOptions: { label: string; value: number }[];
    sortBy: { label: string; value: VotingFormDataType[(typeof URLKeys)['sortBy']] }[];
};

const sortingOptionsDict = {
    nejnovejsi: (a: any, b: any) => b.voteId - a.voteId,
    nejstarsi: (a: any, b: any) => a.voteId - b.voteId
};

type Props = {
    setFilterFunc: (filterFunc: () => (a: any, b: any) => number) => void;
};
const VotingDataCardsFilter = ({ setFilterFunc }: Props) => {
    const { t } = useTranslation();
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dropdownOptions, setDropdownOptions] = useState<DropdownOptionsType>({
        meeting: [],
        member: [],
        otherVoteOptions: [],
        sortBy: [
            { label: t('latest-sort'), value: 'nejnovejsi' },
            { label: t('oldest-sort'), value: 'nejstarsi' }
        ]
    });
    const initialFormState: VotingFormDataType = {
        [URLKeys.voteDescription]: searchParams.get(URLKeys.voteDescription) || '',
        [URLKeys.meetingNumber]: searchParams.get(URLKeys.meetingNumber)
            ? Number(searchParams.get(URLKeys.meetingNumber))
            : null,
        [URLKeys.memberNumber]: searchParams.get(URLKeys.memberNumber)
            ? Number(searchParams.get(URLKeys.memberNumber))
            : null,
        [URLKeys.memberVote]:
            (searchParams.get(URLKeys.memberVote) as VotingFormDataType[(typeof URLKeys)['memberVote']]) || 'all',
        [URLKeys.voteResult]:
            (searchParams.get(URLKeys.voteResult) as VotingFormDataType[typeof URLKeys.voteResult]) || 'all',
        [URLKeys.sortBy]:
            (searchParams.get(URLKeys.sortBy) as VotingFormDataType[(typeof URLKeys)['sortBy']]) || 'nejnovejsi',
        [URLKeys.show]: searchParams.get(URLKeys.show) ? searchParams.get(URLKeys.show)!.split(',') : []
    };

    const [formData, setFormData] = useState<VotingFormDataType>(initialFormState);

    const show = formData[URLKeys.show];
    const sortBy = formData[URLKeys.sortBy];
    const voteDescription = formData[URLKeys.voteDescription];
    const meetingNumber = formData[URLKeys.meetingNumber];
    const memberNumber = formData[URLKeys.memberNumber];
    const memberVote = formData[URLKeys.memberVote];
    const voteResult = formData[URLKeys.voteResult];

    const onInputChange = (
        e: React.ChangeEvent<HTMLInputElement> | DropdownChangeParams | CheckboxChangeParams | any
    ) => {
        const { id, value } = e.target;
        let updatedFormData = { ...formData };
        let updatedSearchParams = new URLSearchParams(searchParams);

        const handleInputChange = (id: string, value: any) => {
            updatedFormData = { ...formData, [id]: value };
            updateSearchParams(id, value, value && value !== 'all' && value !== '');

            // Reset memberVote if memberNumber is cleared
            if (id === URLKeys.memberNumber) {
                updatedFormData[URLKeys.memberVote] = 'all';
                updatedSearchParams.delete(URLKeys.memberVote);
                if (!value) {
                    updatedSearchParams.delete(URLKeys.memberNumber);
                }
            }
            if (id === URLKeys.sortBy && (value === 'nejnovejsi' || value === 'nejstarsi')) {
                setFilterFunc(() => sortingOptionsDict[value as 'nejnovejsi' | 'nejstarsi']);
            }
        };

        const handleSelectButtonChange = (id: string, value: any) => {
            updatedFormData = { ...formData, [id]: value };
            updateSearchParams(id, value.join(','), value.length > 0);
        };

        const updateSearchParams = (key: string, value: string, condition: boolean) => {
            if (condition) {
                if (key === URLKeys.sortBy && value === 'nejnovejsi') {
                    updatedSearchParams.delete(key);
                } else {
                    updatedSearchParams.set(key, value);
                }
            } else {
                updatedSearchParams.delete(key);
            }
        };

        if (id === URLKeys.show) {
            handleSelectButtonChange(id, value);
        } else {
            // voting related selectBtn defaults to 'all' if no value is selected
            if ((id === URLKeys.memberVote || id === URLKeys.voteResult) && value === null) {
                handleInputChange(id, 'all');
            } else {
                handleInputChange(id, value);
            }
        }

        // memberVote needs to be a number if > 2 because of difference between selectBtn and dropdown
        if (id === URLKeys.memberVote && value > 2) {
            updatedFormData[URLKeys.memberVote] = Number(value);
        } else if (id === URLKeys.memberVote && value !== null) {
            updatedFormData[URLKeys.memberVote] = value.toString();
        }

        // reset voteDescriptionText if fullText is selected and clicked again
        if (id === URLKeys.voteDescriptionText) {
            if (value === URLKeys.fullText && formData[URLKeys.voteDescriptionText] === URLKeys.fullText) {
                updatedFormData[URLKeys.voteDescriptionText] = null;
                updatedSearchParams.delete(URLKeys.voteDescriptionText);
            }
        }

        setFormData(updatedFormData);
        Object.entries(updatedFormData).forEach(([key, val]) => {
            if (val !== undefined && val !== 'all' && val !== '') {
                if (val !== null) {
                    updateSearchParams(key, val.toString(), true);
                }
            }
        });

        if (Array.isArray(updatedFormData[URLKeys.show])) {
            if (updatedFormData[URLKeys.show]?.length === 0) {
                updatedSearchParams.delete(URLKeys.show);
            }
        }
        // Preserve the page parameter unless explicitly reset
        if (id !== 'page') {
            updatedSearchParams.delete(URLKeys.page);
        }

        setSearchParams(updatedSearchParams, { preventScrollReset: true, replace: true });
    };

    const getDropdownOptions = async () => {
        try {
            const [sessionResponse, memberResponse] = await Promise.all([
                axios.get(`FE/${municipalitaId}/${bodyType}/${zastupitelstvoId}/dropdownSessionOptions`),
                axios.get(`FE/${municipalitaId}/${bodyType}/${zastupitelstvoId}/dropdownMemberOptions`)
            ]);
            const { data: meetingData }: { data: DropdownOptionType[] } = sessionResponse;
            const { data: memberData }: { data: DropdownOptionType[] } = memberResponse;

            setDropdownOptions((prevOptions) => ({
                ...prevOptions,
                meeting: meetingData.map((item) => ({ label: item.name, value: item.code })),
                member: memberData.map((item) => ({ label: item.name, value: item.code }))
            }));

            if (memberNumber) {
                await getOtherVoteOptions();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getOtherVoteOptions = async () => {
        try {
            const { data: otherVoteOptionsData }: { data: DropdownOptionType[] } = await axios.get(
                `FE/${municipalitaId}/${bodyType}/dropdownOtherVoteOptions/${memberNumber}`
            );
            setDropdownOptions((prevOptions) => ({
                ...prevOptions,
                otherVoteOptions: otherVoteOptionsData.map((item) => ({
                    label: capitalizeFirstLetter(item.name),
                    value: item.code
                }))
            }));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getDropdownOptions();
        if (sortBy) {
            if (typeof sortBy === 'string' && (sortBy === 'nejnovejsi' || sortBy === 'nejstarsi')) {
                setFilterFunc(() => sortingOptionsDict[sortBy]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (memberNumber) {
            getOtherVoteOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberNumber]);

    useEffect(() => {
        setFormData({
            ...formData,
            [URLKeys.voteDescription]: searchParams.get(URLKeys.voteDescription) || '',
            [URLKeys.meetingNumber]: searchParams.get(URLKeys.meetingNumber)
                ? Number(searchParams.get(URLKeys.meetingNumber))
                : null,
            [URLKeys.memberNumber]: searchParams.get(URLKeys.memberNumber)
                ? Number(searchParams.get(URLKeys.memberNumber))
                : null,
            [URLKeys.memberVote]: (() => {
                const memberVote = searchParams.get(URLKeys.memberVote);
                return memberVote && Number(memberVote) > 2
                    ? Number(memberVote)
                    : (memberVote as VotingFormDataType[(typeof URLKeys)['memberVote']]) || 'all';
            })(),
            [URLKeys.voteResult]:
                (searchParams.get(URLKeys.voteResult) as VotingFormDataType[typeof URLKeys.voteResult]) || 'all',
            [URLKeys.show]: searchParams.get(URLKeys.show) ? searchParams.get(URLKeys.show)!.split(',') : []
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const selectBoxOptionItem = (option: { label: string; value: string; icon?: string }) => {
        return (
            <>
                {option.icon && <span className='material-symbols-rounded'>{option.icon}</span>}
                <span className='p-button-label p-c'>{option.label}</span>
            </>
        );
    };

    return (
        <div className='votingDataCardsFilter'>
            <span className='p-float-label'>
                <InputText id={URLKeys.voteDescription} value={voteDescription ?? ''} onChange={onInputChange} />
                <label htmlFor={URLKeys.voteDescription}>{t('voting-description')}</label>
            </span>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.meetingNumber}
                    value={meetingNumber}
                    onChange={onInputChange}
                    options={dropdownOptions.meeting}
                    showClear
                />
                <label htmlFor={URLKeys.meetingNumber}>{t('meeting')}</label>
            </span>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.memberNumber}
                    value={memberNumber}
                    onChange={onInputChange}
                    options={dropdownOptions.member}
                    showClear
                />
                <label htmlFor={URLKeys.memberNumber}>{t('select-member')}</label>
            </span>
            <div className='selectButton-group'>
                <span className='selectButton-wrapper'>
                    <h6>{t('also-show')}</h6>
                    <SelectButton
                        id={URLKeys.show}
                        value={show}
                        itemTemplate={selectBoxOptionItem}
                        options={[
                            {
                                label: capitalizeFirstLetter(t('secret-option')),
                                value: URLKeys.VotingTypes.secretVotes
                            },
                            {
                                label: capitalizeFirstLetter(t('invalid-option')),
                                value: URLKeys.VotingTypes.invalidVotes
                            },
                            {
                                label: capitalizeFirstLetter(t('procedural-option')),
                                value: URLKeys.VotingTypes.proceduralVotes
                            }
                        ]}
                        onChange={onInputChange}
                        multiple
                    />
                </span>
                <span className='selectButton-wrapper'>
                    <h6>{t('voting-result')}</h6>
                    <SelectButton
                        id={URLKeys.voteResult}
                        className='coloredOptions'
                        value={voteResult}
                        itemTemplate={selectBoxOptionItem}
                        options={[
                            { label: capitalizeFirstLetter(t('all')), value: 'all' },
                            { label: capitalizeFirstLetter(t('accepted')), value: '1', icon: 'check_box' },
                            { label: capitalizeFirstLetter(t('rejected')), value: '2', icon: 'disabled_by_default' }
                        ]}
                        onChange={onInputChange}
                    />
                </span>
                {memberNumber && dropdownOptions.otherVoteOptions.length > 0 && (
                    <span
                        className={`p-float-label other-vote-options ${
                            memberNumber && dropdownOptions.otherVoteOptions.length > 0 ? 'fade-in' : 'fade-out'
                        }`}
                    >
                        <>
                            <Dropdown
                                id={URLKeys.memberVote}
                                value={typeof memberVote === 'number' && memberVote > 2 ? memberVote : null}
                                onChange={onInputChange}
                                options={dropdownOptions.otherVoteOptions}
                            />
                            <label htmlFor={URLKeys.memberVote}>{t('other-vote-options')}</label>
                        </>
                    </span>
                )}
                <span className={`selectButton-wrapper ${memberNumber ? 'fade-in' : 'fade-out'}`}>
                    {memberNumber && (
                        <>
                            <h6>{capitalizeFirstLetter(t('voting-option'))}</h6>
                            <SelectButton
                                id={URLKeys.memberVote}
                                className='coloredOptions'
                                value={typeof memberVote === 'number' && memberVote > 2 ? null : memberVote}
                                itemTemplate={selectBoxOptionItem}
                                options={[
                                    { label: capitalizeFirstLetter(t('all')), value: 'all' },
                                    { label: capitalizeFirstLetter(t('yes')), value: '1', icon: 'check_box' },
                                    {
                                        label: capitalizeFirstLetter(t('no')),
                                        value: '2',
                                        icon: 'disabled_by_default'
                                    }
                                ]}
                                onChange={onInputChange}
                            />
                        </>
                    )}
                </span>
            </div>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.sortBy}
                    value={sortBy}
                    onChange={onInputChange}
                    options={dropdownOptions.sortBy}
                />
                <label htmlFor={URLKeys.sortBy}>{t('sort-by')}</label>
            </span>
            <Button
                label={t('resetFilter')}
                className={`${searchParams.size === 0 ? 'fade-out' : 'fade-in'}`}
                icon='pi pi-refresh'
                onClick={() => {
                    const resetFormState = {
                        ...initialFormState,
                        [URLKeys.sortBy]: 'nejnovejsi' as 'nejnovejsi' | 'nejstarsi'
                    };
                    setFormData(resetFormState);
                    setSearchParams(new URLSearchParams(), { preventScrollReset: true, replace: true });
                    setFilterFunc(() => sortingOptionsDict['nejnovejsi']);
                }}
            />
            <span
                className={`p-float-label other-vote-options ${
                    memberNumber && dropdownOptions.otherVoteOptions.length > 0 ? 'fade-in' : 'fade-out'
                }`}
            >
                {memberNumber && (
                    <>
                        <Dropdown
                            id={URLKeys.memberVote}
                            value={typeof memberVote === 'number' && memberVote > 2 ? memberVote : null}
                            onChange={onInputChange}
                            options={dropdownOptions.otherVoteOptions}
                        />
                        <label htmlFor={URLKeys.memberVote}>{t('other-vote-options')}</label>
                    </>
                )}
            </span>
            <div>
                <Checkbox
                    id={URLKeys.voteDescriptionText}
                    value={URLKeys.fullText}
                    onChange={onInputChange}
                    checked={formData[URLKeys.voteDescriptionText] === URLKeys.fullText || false}
                />
                <label htmlFor={URLKeys.voteDescriptionText} className='ml-2'>
                    {t('show-full-description')}
                </label>
            </div>
        </div>
    );
};

export default VotingDataCardsFilter;
